import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, BookOpen, Eye, Layers, Loader2, Lock, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { lehrplanAPI } from '@/lib/api';

/* Lehrplan (15.10.2026, Betreiber: „Baue im CRM eine Seite für den Lehrplan“): je Gürtel, was bis zur Prüfung sitzen
   muss. Gürtel in der Reihenfolge der Gürtelleiter; die Reihenfolge der Inhalte hier ist die Reihenfolge in der App.
   Titel und Beschreibung sehen die Mitglieder (App fragt mit dem Farbnamen, der Server übersetzt) und die Trainer;
   Hinweise für Trainer zeigt bisher nur diese Seite — Trainer-App und Planer-Auswahl blenden sie aus.
   Lesen: kursplan.view, Ändern: settings.edit_general — der Server sagt mit `darf_bearbeiten`, welche Knöpfe es gibt.
   Was ein Trainingsplan eingeplant hat, lässt sich nicht löschen (der Server antwortet 409 mit dem Namen des Plans). */

const OHNE = 'ohne';
const GRENZEN = { title: 200, description: 4000, trainer_notes: 4000, disziplin: 60 };
const feld = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

function fehlertext(e, ersatz) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return 'Bitte die Eingaben prüfen: ein Feld ist leer oder zu lang.';
  return ersatz;
}

function Punkt({ farbe }) {
  return <span aria-hidden className="inline-block h-3 w-3 shrink-0 rounded-full border border-border" style={{ backgroundColor: farbe || 'transparent' }} />;
}

function planNamen(zyklen) {
  return zyklen.map((z) => `„${z.name}“`).join(', ');
}

/* ── Anlegen / Bearbeiten ── */

function InhaltDialog({ auftrag, guertel, disziplinen, onZu, onGespeichert }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={!!auftrag} onOpenChange={(o) => { if (!o && !busy) onZu(); }}>
      <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)] rounded-xl" data-testid="lehrplan-dialog">
        {/* Der Inhalt wird beim Öffnen neu aufgebaut (Radix entfernt ihn beim Schließen) — so startet das Formular
            immer mit dem Stand des gewählten Eintrags. */}
        {auftrag && <InhaltFormular auftrag={auftrag} guertel={guertel} disziplinen={disziplinen} busy={busy} setBusy={setBusy}
          onZu={onZu} onGespeichert={onGespeichert} />}
      </DialogContent>
    </Dialog>
  );
}

function InhaltFormular({ auftrag, guertel, disziplinen, busy, setBusy, onZu, onGespeichert }) {
  const eintrag = auftrag.eintrag || null;
  const gueltig = useMemo(() => new Set(guertel.map((g) => g.key)), [guertel]);
  const [titel, setTitel] = useState(eintrag?.title || '');
  const [beschreibung, setBeschreibung] = useState(eintrag?.description || '');
  const [hinweise, setHinweise] = useState(eintrag?.trainer_notes || '');
  const [disziplin, setDisziplin] = useState(eintrag?.disziplin || '');
  const [belt, setBelt] = useState(() => {
    const start = eintrag ? eintrag.belt : auftrag.belt;
    return gueltig.has(start) ? start : (eintrag ? '' : guertel[0]?.key || '');
  });
  const [fehler, setFehler] = useState('');

  const wechsel = !!eintrag && belt && belt !== eintrag.belt;
  const betroffen = wechsel ? (eintrag.verwendet_in || []).filter((z) => !(z.belts || []).includes(belt)) : [];
  const vorschlaege = disziplinen.filter((x) => x.toLowerCase() !== disziplin.trim().toLowerCase()).slice(0, 8);
  const ok = titel.trim().length > 0 && !!belt && betroffen.length === 0 && !busy;

  const speichern = async () => {
    if (!ok) return;
    setBusy(true); setFehler('');
    const daten = { title: titel.trim(), description: beschreibung, trainer_notes: hinweise, disziplin };
    try {
      let r;
      if (eintrag) {
        r = await lehrplanAPI.aendern(eintrag.id, wechsel || !gueltig.has(eintrag.belt) ? { ...daten, belt } : daten);
      } else {
        r = await lehrplanAPI.anlegen({ ...daten, belt });
      }
      setBusy(false);
      toast.success(eintrag ? 'Inhalt gespeichert' : 'Inhalt angelegt');
      onGespeichert(r.data);
    } catch (e) {
      setFehler(fehlertext(e, 'Das hat nicht geklappt. Bitte noch einmal versuchen.'));
      setBusy(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{eintrag ? 'Inhalt bearbeiten' : 'Inhalt hinzufügen'}</DialogTitle>
        <DialogDescription>Beim jeweiligen Feld steht, wer es zu sehen bekommt.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="lp-titel">Titel</Label>
          <Input id="lp-titel" value={titel} maxLength={GRENZEN.title} autoFocus placeholder="z. B. Jab, Teep, Armhebel aus der Guard"
            onChange={(e) => setTitel(e.target.value)} data-testid="lehrplan-titel" />
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Eye className="h-3 w-3" /> sehen Mitglieder in der App und die Trainer</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lp-beschreibung">Beschreibung</Label>
          <Textarea id="lp-beschreibung" rows={3} value={beschreibung} maxLength={GRENZEN.description}
            placeholder="Worauf es ankommt — so, wie es ein Mitglied verstehen soll" onChange={(e) => setBeschreibung(e.target.value)}
            data-testid="lehrplan-beschreibung" />
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Eye className="h-3 w-3" /> sehen Mitglieder in der App und die Trainer</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lp-hinweise">Hinweise für Trainer</Label>
          <Textarea id="lp-hinweise" rows={2} value={hinweise} maxLength={GRENZEN.trainer_notes}
            placeholder="z. B. häufige Fehler, worauf ihr bei der Prüfung achtet" onChange={(e) => setHinweise(e.target.value)}
            data-testid="lehrplan-hinweise" />
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> sieht nur das Team hier im CRM — nie die Mitglieder</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="lp-disziplin">Disziplin</Label>
            <Input id="lp-disziplin" list="lehrplan-disziplinen" value={disziplin} maxLength={GRENZEN.disziplin} placeholder="z. B. BMT"
              onChange={(e) => setDisziplin(e.target.value)} data-testid="lehrplan-disziplin" />
            <datalist id="lehrplan-disziplinen">{disziplinen.map((x) => <option key={x} value={x} />)}</datalist>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lp-guertel">Gürtel</Label>
            <select id="lp-guertel" className={feld} value={belt} onChange={(e) => setBelt(e.target.value)} data-testid="lehrplan-guertel-wahl">
              {!belt && <option value="" disabled>Bitte Gürtel wählen</option>}
              {guertel.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </div>
        </div>
        {vorschlaege.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 -mt-2">
            <span className="text-[11px] text-muted-foreground">Bisher verwendet:</span>
            {vorschlaege.map((x) => (
              <button key={x} type="button" onClick={() => setDisziplin(x)}
                className="p1-knopf rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-marke hover:text-foreground">{x}</button>
            ))}
          </div>
        )}
        {eintrag && !gueltig.has(eintrag.belt) && (
          <p className="rounded-md bg-muted px-3 py-2 text-xs">
            Bisher eingetragen: {eintrag.belt ? `„${eintrag.belt}“ — diesen Gürtel gibt es in der Gürtelleiter nicht (mehr)` : 'kein Gürtel'}.
            Mit einem Gürtel aus der Liste erscheint der Inhalt bei diesem Gürtel in der App, in der Trainer-App und im Trainingsplaner.
          </p>
        )}
        {betroffen.length > 0 && (
          <p className="flex gap-2 rounded-md bg-[hsl(var(--warning)/0.12)] px-3 py-2 text-xs" data-testid="lehrplan-wechsel-gesperrt">
            <TriangleAlert className="h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
            <span>Eingeplant in {planNamen(betroffen)}. {betroffen.length === 1 ? 'Dieser Plan hat' : 'Diese Pläne haben'} den neuen Gürtel nicht — der Inhalt fiele dort heraus. Nimm ihn zuerst im Trainingsplaner heraus oder ergänze dort den Gürtel.</span>
          </p>
        )}
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">{fehler}</p>}
      </div>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" onClick={onZu} disabled={busy}>Abbrechen</Button>
        <Button onClick={speichern} disabled={!ok} data-testid="lehrplan-speichern">
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{eintrag ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </DialogFooter>
    </>
  );
}

/* ── Löschen (eigener Dialog statt window.confirm) ── */

function LoeschenDialog({ eintrag, onZu, onGeloescht }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={!!eintrag} onOpenChange={(o) => { if (!o && !busy) onZu(); }}>
      <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] rounded-xl" data-testid="lehrplan-loeschen-dialog">
        {eintrag && <LoeschenInhalt eintrag={eintrag} busy={busy} setBusy={setBusy} onZu={onZu} onGeloescht={onGeloescht} />}
      </DialogContent>
    </Dialog>
  );
}

function LoeschenInhalt({ eintrag, busy, setBusy, onZu, onGeloescht }) {
  const [fehler, setFehler] = useState('');
  const gesperrt = (eintrag.verwendet_in || []).length > 0;

  const loeschen = async () => {
    setBusy(true); setFehler('');
    try {
      await lehrplanAPI.loeschen(eintrag.id);
      setBusy(false);
      toast.success(`„${eintrag.title}“ gelöscht`);
      onGeloescht();
    } catch (e) {
      // 409: inzwischen in einem Trainingsplan eingeplant — der Server nennt den Plan
      setFehler(fehlertext(e, 'Das Löschen hat nicht geklappt.'));
      setBusy(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{gesperrt ? 'Löschen geht gerade nicht' : `„${eintrag.title}“ löschen?`}</DialogTitle>
        <DialogDescription>
          {gesperrt
            ? `Der Inhalt ist eingeplant in ${planNamen(eintrag.verwendet_in)}. Nimm ihn dort zuerst heraus (Trainingsplaner), dann lässt er sich löschen.`
            : 'Der Inhalt verschwindet aus der App der Mitglieder und aus der Trainer-App. Das lässt sich nicht rückgängig machen.'}
        </DialogDescription>
      </DialogHeader>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">{fehler}</p>}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" onClick={onZu} disabled={busy}>{gesperrt || fehler ? 'Schließen' : 'Abbrechen'}</Button>
        {!gesperrt && !fehler && (
          <Button variant="destructive" onClick={loeschen} disabled={busy} data-testid="lehrplan-loeschen-bestaetigen">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Löschen
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

/* ── ein Inhalt ── */

function InhaltZeile({ e, nr, darf, sortierbar, erster, letzter, busy, ohneGueltig, onHoch, onRunter, onBearbeiten, onLoeschen }) {
  const knopf = 'h-8 w-8 p-0';
  const pfeil = `${knopf} aria-disabled:cursor-wait aria-disabled:opacity-50`;
  return (
    <li className="flex flex-wrap gap-x-3 gap-y-2 py-3 sm:flex-nowrap" data-testid={`lehrplan-eintrag-${e.id}`}>
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-marke/10 text-xs font-bold tabular-nums text-marke">{nr}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="break-words font-semibold">{e.title}</span>
          {e.disziplin && <Badge variant="outline" className="text-[10px] font-medium">{e.disziplin}</Badge>}
          {(e.verwendet_in || []).length > 0 && (
            <Badge variant="outline" className="gap-1 border-marke/40 text-[10px] font-medium" title={`Eingeplant in ${planNamen(e.verwendet_in)}`}>
              <Layers className="h-3 w-3" /> im Trainingsplan
            </Badge>
          )}
        </div>
        {e.description
          ? <p className="mt-0.5 whitespace-pre-line break-words text-sm text-muted-foreground">{e.description}</p>
          : <p className="mt-0.5 text-xs italic text-muted-foreground">Keine Beschreibung — Mitglieder sehen nur den Titel.</p>}
        {e.trainer_notes && (
          <p className="mt-2 flex gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs">
            <Lock className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="whitespace-pre-line break-words"><span className="font-medium">Hinweis für Trainer:</span> {e.trainer_notes}</span>
          </p>
        )}
        {(e.verwendet_in || []).length > 0 && (
          <p className="mt-1 text-[11px] text-muted-foreground">Eingeplant in {planNamen(e.verwendet_in)}</p>
        )}
        {ohneGueltig && (
          <p className="mt-1 text-[11px] text-muted-foreground">Eingetragener Gürtel: {e.belt ? `„${e.belt}“` : 'keiner'}</p>
        )}
      </div>
      {darf && (
        <div className="flex w-full shrink-0 items-start gap-1 pl-10 sm:w-auto sm:pl-0">
          {sortierbar && (
            <>
              {/* Während des Speicherns nur aria-disabled statt disabled: ein gesperrter Knopf verliert den Fokus. */}
              <Button variant="ghost" className={pfeil} disabled={erster} aria-disabled={busy || undefined} onClick={onHoch} aria-label="Nach oben" title="Nach oben" data-testid={`lehrplan-hoch-${e.id}`}><ArrowUp className="h-4 w-4" /></Button>
              <Button variant="ghost" className={pfeil} disabled={letzter} aria-disabled={busy || undefined} onClick={onRunter} aria-label="Nach unten" title="Nach unten" data-testid={`lehrplan-runter-${e.id}`}><ArrowDown className="h-4 w-4" /></Button>
            </>
          )}
          <Button variant="ghost" className={knopf} onClick={onBearbeiten} aria-label="Bearbeiten" title="Bearbeiten" data-testid={`lehrplan-bearbeiten-${e.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" className={`${knopf} text-muted-foreground hover:text-destructive`} onClick={onLoeschen} aria-label="Löschen" title="Löschen" data-testid={`lehrplan-loeschen-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      )}
    </li>
  );
}

/* ── Seite ── */

export default function LehrplanPage() {
  const [d, setD] = useState(null);
  const [ladeFehler, setLadeFehler] = useState('');
  const [params, setParams] = useSearchParams();
  const [disziplin, setDisziplin] = useState('');
  const [auftrag, setAuftrag] = useState(null);       // { belt } = neu, { eintrag } = bearbeiten
  const [zumLoeschen, setZumLoeschen] = useState(null);
  const [sortiert, setSortiert] = useState(false);
  const sortiertRef = useRef(false);   // sperrt einen zweiten Klick, bevor der neue Stand gezeichnet ist
  const fokusRef = useRef(null);       // { id, richtung } — Pfeil, der nach dem Tausch den Fokus zurückbekommt
  const geladenRef = useRef(false);

  const laden = useCallback(async () => {
    try { const r = await lehrplanAPI.seite(); setD(r.data); setLadeFehler(''); geladenRef.current = true; }
    catch (e) {
      const text = fehlertext(e, 'Der Lehrplan konnte nicht geladen werden.');
      setLadeFehler(text);
      // Steht schon ein Stand da, sieht man ladeFehler nicht — sonst bliebe die Liste still auf dem alten Stand
      if (geladenRef.current) {
        toast.error(`${text} Die Liste zeigt eventuell nicht den neuesten Stand.`, {
          id: 'lehrplan-laden', action: { label: 'Erneut versuchen', onClick: () => laden() },
        });
      }
    }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  // Nach „Nach oben/unten“ hängt React den Eintrag im Browser um — der gedrückte Knopf verliert dabei den Fokus.
  // Darum den Fokus zurückgeben; ist der Pfeil am Rand gesperrt, auf den anderen Pfeil desselben Eintrags.
  // Ohne Abhängigkeiten: läuft nach jedem Zeichnen, auch wenn nach einem Fehler nur der Ladekreis verschwindet.
  useEffect(() => {
    const f = fokusRef.current;
    if (!f) return;
    fokusRef.current = null;
    const knopf = (r) => document.querySelector(`[data-testid="lehrplan-${r}-${f.id}"]`);
    const ziel = knopf(f.richtung);
    (ziel && !ziel.disabled ? ziel : knopf(f.richtung === 'hoch' ? 'runter' : 'hoch'))?.focus();
  });

  const guertel = useMemo(() => d?.guertel || [], [d]);
  const ohne = useMemo(() => d?.ohne_gueltigen_guertel || [], [d]);
  const darf = !!d?.darf_bearbeiten;
  const gesamt = guertel.reduce((n, g) => n + g.inhalte.length, 0) + ohne.length;

  const gewaehlt = useMemo(() => {
    const p = params.get('guertel');
    if (p === OHNE && ohne.length) return OHNE;
    if (guertel.some((g) => g.key === p)) return p;
    return (guertel.find((g) => g.inhalte.length) || guertel[0])?.key || null;
  }, [params, guertel, ohne.length]);

  const waehle = (key) => {
    setDisziplin('');
    setParams((alt) => { const n = new URLSearchParams(alt); n.set('guertel', key); return n; }, { replace: true });
  };

  const gruppe = gewaehlt === OHNE ? null : guertel.find((g) => g.key === gewaehlt);
  const inhalte = gewaehlt === OHNE ? ohne : (gruppe?.inhalte || []);
  const imGuertel = [...new Set(inhalte.map((e) => e.disziplin).filter(Boolean))];
  const filter = imGuertel.includes(disziplin) ? disziplin : '';
  const sichtbar = filter ? inhalte.filter((e) => e.disziplin === filter) : inhalte;
  const sortierbar = darf && gewaehlt !== OHNE && !filter && inhalte.length > 1;

  const verschieben = async (i, richtung) => {
    const j = i + richtung;
    if (j < 0 || j >= inhalte.length || sortiertRef.current) return;
    const neu = [...inhalte];
    const id = neu[i].id;
    const pfeilRichtung = richtung < 0 ? 'hoch' : 'runter';
    [neu[i], neu[j]] = [neu[j], neu[i]];
    sortiertRef.current = true;
    fokusRef.current = { id, richtung: pfeilRichtung };
    setD((alt) => ({ ...alt, guertel: alt.guertel.map((g) => (g.key === gewaehlt ? { ...g, inhalte: neu } : g)) }));
    setSortiert(true);
    try { await lehrplanAPI.reihenfolge(gewaehlt, neu.map((e) => e.id)); }
    catch (e) {
      toast.error(fehlertext(e, 'Die Reihenfolge wurde nicht gespeichert.'));
      fokusRef.current = { id, richtung: pfeilRichtung };
      await laden();
    }
    finally { sortiertRef.current = false; setSortiert(false); }
  };

  const gespeichert = async (eintrag) => {
    setAuftrag(null);
    await laden();
    if (eintrag?.belt && eintrag.belt !== gewaehlt) waehle(eintrag.belt);
  };

  if (!d) {
    return (
      <div className="max-w-5xl space-y-4" data-testid="lehrplan-seite">
        <h1 className="flex items-center gap-2 text-2xl font-bold"><BookOpen className="h-6 w-6 text-marke" /> Lehrplan</h1>
        {ladeFehler
          ? <Card><CardContent className="space-y-3 p-5"><p className="text-sm">{ladeFehler}</p><Button variant="outline" size="sm" onClick={laden}>Erneut versuchen</Button></CardContent></Card>
          : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …</div>}
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-5" data-testid="lehrplan-seite">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-bold"><BookOpen className="h-6 w-6 text-marke" /> Lehrplan</h1>
          <p className="mt-1 text-sm text-muted-foreground">Was je Gürtel bis zur Prüfung sitzen muss. Die Reihenfolge hier ist die Reihenfolge in der App.</p>
        </div>
        {darf && (
          <Button onClick={() => setAuftrag({ belt: gewaehlt === OHNE ? guertel[0]?.key : gewaehlt })} data-testid="lehrplan-neu">
            <Plus className="mr-1 h-4 w-4" /> Inhalt hinzufügen
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="grid gap-2.5 p-3.5 text-xs sm:grid-cols-3 sm:gap-3 sm:p-4">
          <div className="flex gap-2"><Eye className="mt-0.5 h-4 w-4 shrink-0 text-marke" />
            <p><span className="font-semibold">Titel und Beschreibung</span> sehen Mitglieder in der App (Reiter Training) und die Trainer.</p></div>
          <div className="flex gap-2"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-marke" />
            <p><span className="font-semibold">Hinweise für Trainer</span> sieht nur das Team hier im CRM — nie die Mitglieder.</p></div>
          <div className="flex gap-2"><Layers className="mt-0.5 h-4 w-4 shrink-0 text-marke" />
            <p><span className="font-semibold">Im Trainingsplan</span> eingeplante Inhalte lassen sich nicht löschen.</p></div>
        </CardContent>
      </Card>

      {!darf && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Nur lesen — ändern kann, wer im Team das Recht „Allgemeine Einstellungen bearbeiten“ hat.</p>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Gürtel">
        {guertel.map((g) => {
          const aktiv = g.key === gewaehlt;
          return (
            <button key={g.key} type="button" role="tab" aria-selected={aktiv} onClick={() => waehle(g.key)} data-testid={`lehrplan-guertel-${g.key}`}
              className={`p1-knopf inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs sm:h-9 sm:gap-2 sm:px-3 sm:text-sm ${aktiv ? 'border-marke bg-marke/10 font-semibold text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
              <Punkt farbe={g.farbe} /> {g.label}
              <span className="text-xs tabular-nums text-muted-foreground">{g.inhalte.length}</span>
            </button>
          );
        })}
        {ohne.length > 0 && (
          <button type="button" role="tab" aria-selected={gewaehlt === OHNE} onClick={() => waehle(OHNE)} data-testid="lehrplan-guertel-ohne"
            className={`p1-knopf inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs sm:h-9 sm:gap-2 sm:px-3 sm:text-sm ${gewaehlt === OHNE ? 'border-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.12)] font-semibold' : 'border-[hsl(var(--warning)/0.5)] bg-card text-muted-foreground hover:text-foreground'}`}>
            <TriangleAlert className="h-4 w-4 text-[hsl(var(--warning))]" /> Ohne gültigen Gürtel
            <span className="text-xs tabular-nums">{ohne.length}</span>
          </button>
        )}
      </div>

      <Card>
        <CardContent className="p-4 sm:p-5">
          {gewaehlt === OHNE ? (
            <div className="mb-2">
              <h2 className="flex items-center gap-2 text-lg font-bold"><TriangleAlert className="h-5 w-5 text-[hsl(var(--warning))]" /> Ohne gültigen Gürtel</h2>
              <p className="mt-1 text-sm text-muted-foreground">Diese Inhalte hängen an einem Gürtel, den es in der Gürtelleiter nicht (mehr) gibt. Trainer-App und Trainingsplaner zeigen sie bei keinem Gürtel. {darf ? 'Weise ihnen über „Bearbeiten“ einen Gürtel zu oder lösche sie.' : ''}</p>
            </div>
          ) : gruppe && (
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="flex items-center gap-2 text-lg font-bold"><Punkt farbe={gruppe.farbe} /> {gruppe.label}</h2>
              <span className="text-xs text-muted-foreground">
                {gruppe.ab_einheiten > 0 ? `ab ${gruppe.ab_einheiten} Einheiten · ` : ''}{gruppe.inhalte.length} {gruppe.inhalte.length === 1 ? 'Inhalt' : 'Inhalte'}
              </span>
              {sortiert && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Reihenfolge wird gespeichert" />}
            </div>
          )}

          {imGuertel.length > 1 && (
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              {['', ...imGuertel].map((x) => (
                <Button key={x || 'alle'} size="sm" variant={filter === x ? 'default' : 'outline'} className="h-7 rounded-full px-3 text-xs"
                  onClick={() => setDisziplin(x)}>{x || 'Alle Disziplinen'}</Button>
              ))}
              {filter && darf && gewaehlt !== OHNE && <span className="text-[11px] text-muted-foreground">Zum Sortieren „Alle Disziplinen“ wählen.</span>}
            </div>
          )}

          {sichtbar.length === 0 ? (
            <div className="py-8 text-center" data-testid="lehrplan-leer">
              <BookOpen className="mx-auto h-8 w-8 text-muted-foreground/60" />
              <p className="mt-2 font-semibold">{gesamt === 0 ? 'Noch kein Lehrplan hinterlegt' : `Für den ${gruppe?.label || 'Gürtel'} sind noch keine Inhalte hinterlegt`}</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                {gesamt === 0
                  ? 'Lege je Gürtel fest, was bis zur Prüfung sitzen muss — zum Beispiel „Jab“ oder „Teep“. Mitglieder sehen die Inhalte in der App beim jeweiligen Gürtel, Trainer hängen sie im Trainingsplaner an einzelne Wochen.'
                  : 'Mitglieder sehen in der App bei diesem Gürtel noch nichts.'}
              </p>
              {darf && gruppe && (
                <Button className="mt-4" onClick={() => setAuftrag({ belt: gruppe.key })} data-testid="lehrplan-neu-leer"><Plus className="mr-1 h-4 w-4" /> Ersten Inhalt anlegen</Button>
              )}
            </div>
          ) : (
            <ol className="divide-y divide-border">
              {sichtbar.map((e, i) => (
                <InhaltZeile key={e.id} e={e} nr={i + 1} darf={darf} sortierbar={sortierbar} busy={sortiert}
                  erster={i === 0} letzter={i === sichtbar.length - 1} ohneGueltig={gewaehlt === OHNE}
                  onHoch={() => verschieben(i, -1)} onRunter={() => verschieben(i, 1)}
                  onBearbeiten={() => setAuftrag({ eintrag: e })} onLoeschen={() => setZumLoeschen(e)} />
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <InhaltDialog auftrag={auftrag} guertel={guertel} disziplinen={d.disziplinen || []} onZu={() => setAuftrag(null)} onGespeichert={gespeichert} />
      <LoeschenDialog eintrag={zumLoeschen} onZu={() => setZumLoeschen(null)} onGeloescht={() => { setZumLoeschen(null); laden(); }} />
    </div>
  );
}
